/**
 * RunSummaryTab — composed prose summary from graph steps + run error.
 * No pre-built field in the debugger payload — synthesized from step data.
 */
import React from 'react';
import { Info } from 'lucide-react';
import { type DebugPayload, findFailedStep } from './run-detail-types.js';

interface RunSummaryTabProps {
  payload: DebugPayload;
}

export const RunSummaryTab: React.FC<RunSummaryTabProps> = ({ payload }) => {
  const { run, graph } = payload;
  const steps = graph.steps;
  const failedStep = findFailedStep(steps);

  const completedSteps = steps.filter((s) => s.status === 'completed' || s.status === 'success');
  const skippedSteps = steps.filter((s) => s.status === 'skipped');
  const artifactCount = payload.linkedEntities.artifacts.length;

  // Compose summary prose
  const summaryParts: React.ReactNode[] = [];

  if (completedSteps.length > 0) {
    const okSummaries = completedSteps
      .filter((s) => s.outputSummary)
      .map((s) => s.outputSummary)
      .join('; ');
    summaryParts.push(
      <React.Fragment key="ok">
        {okSummaries
          ? `The run completed ${completedSteps.length} step${completedSteps.length !== 1 ? 's' : ''}: ${okSummaries}.`
          : `The run completed ${completedSteps.length} step${completedSteps.length !== 1 ? 's' : ''} successfully.`}
      </React.Fragment>,
    );
  }

  if (failedStep) {
    summaryParts.push(
      <React.Fragment key="fail">
        {' '}
        It failed at <code className="font-mono text-danger text-[12px]">{failedStep.stepName}</code>
        {failedStep.retryCount > 0 && ` after ${failedStep.retryCount} retries`}
        {failedStep.error ? ` — ${failedStep.error}` : ''}.
      </React.Fragment>,
    );
  }

  if (skippedSteps.length > 0 && failedStep) {
    summaryParts.push(
      <React.Fragment key="skip">
        {' '}
        Downstream {skippedSteps.map((s) => s.stepName).join(', ')} step{skippedSteps.length !== 1 ? 's were' : ' was'}{' '}
        skipped.
      </React.Fragment>,
    );
  }

  if (artifactCount > 0) {
    summaryParts.push(
      <React.Fragment key="artifact">
        {' '}
        {artifactCount} artifact{artifactCount !== 1 ? 's' : ''} produced.
      </React.Fragment>,
    );
  } else if (failedStep) {
    summaryParts.push(<React.Fragment key="noartifact"> No artifact was produced.</React.Fragment>);
  }

  if (!failedStep && completedSteps.length === 0 && steps.length === 0) {
    summaryParts.push(<React.Fragment key="empty">No steps were recorded for this run.</React.Fragment>);
  }

  return (
    <div className="bg-surface border border-border rounded-xl overflow-hidden mb-4">
      {/* Card header */}
      <div className="flex items-center justify-between gap-2.5 px-4 py-3 border-b border-border bg-surface-alt">
        <span className="font-mono text-[10.5px] uppercase tracking-[1.2px] text-muted flex items-center gap-2">
          <Info size={14} className="text-faint" />
          Run summary
        </span>
      </div>

      <div className="p-4">
        <p className="text-[13px] text-body leading-[1.6]">
          {summaryParts.length > 0 ? summaryParts : 'No summary available for this run.'}
        </p>

        {/* Show raw error if present */}
        {run.error && (
          <div className="mt-3">
            <p className="text-[11px] font-mono text-faint uppercase tracking-wider mb-1">Run error</p>
            <code className="block font-mono text-[12px] text-danger bg-bg rounded-sm px-3 py-2 whitespace-pre-wrap">
              {run.error}
            </code>
          </div>
        )}
      </div>
    </div>
  );
};
